import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Tag, PhotoFrame, Reveal, EmptyState } from "@/components/Shared";
import { api, fmtDate } from "@/lib/api";

export default function BlogDetail() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/blog/${id}`)
      .then(({ data }) => setPost(data))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-silver-muted">Loading story…</p>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="mx-auto max-w-3xl px-6 pb-32 pt-48">
        <EmptyState title="Story not found" note="It may have been unpublished." />
        <div className="mt-8 text-center">
          <Link to="/blog" className="btn-silver" data-testid="blog-notfound-back-btn">
            <span>Back to Blog</span>
          </Link>
        </div>
      </main>
    );
  }

  const paragraphs = (post.content || "").split(/\n+/).filter(Boolean);

  return (
    <main className="pb-28">
      <section className="relative overflow-hidden pb-14 pt-40" data-testid="blog-hero">
        <div className="absolute inset-0">
          <img src={post.cover_image || ""} alt="" onError={(e) => (e.target.style.display = "none")} className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/60 to-ink" />
        </div>
        <div className="relative z-10 mx-auto max-w-3xl px-6">
          <Link to="/blog" className="nav-link inline-flex items-center gap-2" data-testid="blog-back-link">
            <ArrowLeft size={14} /> All Stories
          </Link>
          <div className="mt-8"><Tag tone="ember">{post.category}</Tag></div>
          <h1 className="mt-6 font-display text-4xl font-semibold leading-tight text-silver sm:text-5xl">{post.title}</h1>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-silver-muted">
            By {post.author} · {fmtDate(post.date)}
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-6" data-testid="blog-body">
        <PhotoFrame src={post.cover_image} alt={post.title} label="Cover image — add via Admin" className="aspect-video w-full" />
        <div className="mt-12 space-y-6">
          {paragraphs.map((p, i) => (
            <Reveal key={i} delay={i * 0.03}>
              <p className="text-base leading-relaxed text-silver-dim first:font-display first:text-xl first:italic first:text-silver">{p}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-16 flex items-center justify-between border-t border-silver/10 pt-8">
          <p className="eyebrow">Written for RCSCW</p>
          <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-silver-dim hover:text-white" data-testid="blog-more-link">
            More stories <ArrowUpRight size={14} className="text-ember" />
          </Link>
        </div>
      </article>
    </main>
  );
}
